import { Accordion, AccordionDetails, AccordionSummary, Box, Checkbox, FormControl, MenuItem, Select, Typography } from '@mui/material'
import { ChangeEvent, useMemo, useState } from 'react'
import NoData from '../NoData/NoData'
import { checkNoDataDescription, CustomTabPanel } from '../../../../helpers'
import styles from './styles'
import ProductTab from './List/ProductTab'
import { ModalOverlay } from '../../../../helpers/ModalOverlayClick'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { createPortal } from 'react-dom'
import { CreateNewCompany } from '../../Modals'
import ProductFilters from '../../Filters/ProductFilters'
import { Product, SortConfig, SortOrder } from '../../../../shared/types/instances'
import { sortProducts } from '../../../../shared/utils/sort-products'
import AddNewProductDetail from '../../Modals/AddNewProductDetail'
import { ITEMS_PER_PAGE } from '../../../../shared/constants'
import PaginationUI from '../../../ui/pagination'
import { ButtonUI } from '../../../ui'
import { useTranslation } from "react-i18next"
import { useLocation } from "react-router"
import BulkActionsBar from './BulkActionsBar'
import ProductBulk from '../../../../api/repositories/productBulk'
import { toast } from 'react-toastify'
import { getProductsByCompany } from '../../../../app/slices/dashboard'

interface Props {
  hasPagination?: boolean;
  activeTagsIndex?: number[];
  searchValue?: string;
  onImportClick?: () => void;
  onCreateClick?: () => void;
}

const ProductsList = ({
  hasPagination,
  activeTagsIndex = [],
  searchValue,
  onImportClick,
  onCreateClick,
}: Props) => {
  const { t } = useTranslation()
  const { pathname } = useLocation()
  const dispatch = useDispatch()

  const company = useSelector((state: any) => state.dashboard.company, shallowEqual)
  const products = useSelector((state: any) => state.dashboard.products, shallowEqual)

  const [tab, setTab] = useState(0)
  const [isOpen, setIsOpen] = useState(true)
  const [openCreateCompany, setOpenCreateCompany] = useState(false)
  const [openCreateProduct, setOpenCreateProduct] = useState(false)
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [statusFilter, setStatusFilter] = useState<string>('all')

  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: 'date', order: 'desc' })
  const [filter, setFilter] = useState({
    date: { value: 'desc' as SortOrder, sortable: true },
    qrcode: { value: 'asc' as SortOrder, sortable: true },
    verified: { value: 'asc' as SortOrder, sortable: true },
    scanned: { value: 'asc' as SortOrder, sortable: true },
    feedback: { value: 'asc' as SortOrder, sortable: true }
  })

  const tags = Array.from(
    new Set(
      useSelector((state: any) =>
        state.dashboard.products.map((item: any) => item.tags), shallowEqual
      ).flat()
    )
  ) as string[]

  const [page, setPage] = useState(1)

  const handleSortChange = (field: keyof typeof filter) => {
    setFilter((prev) => {
      const currentValue = prev[field].value
      let newValue: SortOrder = 'asc'
      if (currentValue === 'asc') newValue = 'desc'
      else if (currentValue === 'desc') newValue = 'asc'
      const updatedFilter = {
        date: { ...prev.date, value: 'none' as SortOrder },
        qrcode: { ...prev.qrcode, value: 'none' as SortOrder },
        verified: { ...prev.verified, value: 'none' as SortOrder },
        scanned: { ...prev.scanned, value: 'none' as SortOrder },
        feedback: { ...prev.feedback, value: 'none' as SortOrder }
      }
      updatedFilter[field].value = newValue
      let key = ''
      if (field === 'date') key = 'date'
      if (field === 'qrcode') key = 'qrcodes'
      if (field === 'verified') key = 'verified'
      if (field === 'scanned') key = 'scanned'
      if (field === 'feedback') key = 'feedbacks'
      setSortConfig({ key, order: newValue })
      return updatedFilter
    })
  }

  const sortedProducts = useMemo(() => {
    if (!products) return []
    return sortProducts(products, sortConfig)
  }, [products, sortConfig])

  const filteredProducts = useMemo(() => {
    let result: Product[] = sortedProducts as Product[]

    if (statusFilter !== 'all') {
      result = result.filter((p) => p.status === statusFilter)
    }

    if (searchValue && searchValue.trim() !== '') {
      const lowerSearch = searchValue.toLowerCase()
      result = result.filter((product) =>
        product.product_name.toLowerCase().includes(lowerSearch)
      )
    }

    if (activeTagsIndex.length > 0) {
      const lowerActiveTags = activeTagsIndex.map((index: number) =>
        tags[index].toLowerCase()
      )
      result = result.filter((product) =>
        product.tags.some((tag) => lowerActiveTags.includes(tag.toLowerCase()))
      )
    }

    return result
  }, [sortedProducts, searchValue, activeTagsIndex, statusFilter, tags])

  const paginatedProducts = useMemo(() => {
    if (!hasPagination) return filteredProducts
    return filteredProducts.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE)
  }, [filteredProducts, page, hasPagination])

  const handleChangePage = (_: ChangeEvent<unknown>, value: number) => {
    setPage(value)
  }

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    )
  }

  const toggleSelectAll = () => {
    const pageIds = paginatedProducts.map((p) => p._id)
    const allSelected = pageIds.every((id) => selectedIds.includes(id))
    if (allSelected) {
      setSelectedIds((prev) => prev.filter((id) => !pageIds.includes(id)))
    } else {
      setSelectedIds((prev) => Array.from(new Set([...prev, ...pageIds])))
    }
  }

  const refreshProducts = () => {
    dispatch(getProductsByCompany() as any)
  }

  const handleBulkPublish = async () => {
    try {
      await ProductBulk.bulkPublish(selectedIds)
      toast.success(t("bulk_publish_started"))
      setSelectedIds([])
      refreshProducts()
    } catch (e: any) {
      toast.error(e.response?.data?.detail?.Message || t("bulk_load_error"))
    }
  }

  const handleBulkIssueQr = async () => {
    try {
      await ProductBulk.bulkIssueQr(selectedIds)
      toast.success(t("bulk_issue_started"))
      setSelectedIds([])
      refreshProducts()
    } catch (e: any) {
      toast.error(e.response?.data?.detail?.Message || t("bulk_load_error"))
    }
  }

  const handleBulkExport = async () => {
    try {
      await ProductBulk.exportProducts(undefined, selectedIds)
      toast.success(t("bulk_export_started"))
      setSelectedIds([])
    } catch (e: any) {
      toast.error(e.response?.data?.detail?.Message || t("bulk_load_error"))
    }
  }

  const handleBulkDelete = async () => {
    if (!window.confirm(t("bulk_delete_confirm"))) return
    try {
      await ProductBulk.bulkDelete(selectedIds)
      toast.success(t("bulk_delete_done"))
      setSelectedIds([])
      refreshProducts()
    } catch (e: any) {
      toast.error(e.response?.data?.detail?.Message || t("bulk_load_error"))
    }
  }

  const allPageSelected =
    paginatedProducts.length > 0 &&
    paginatedProducts.every((p) => selectedIds.includes(p._id))

  return (
    <Accordion variant="outlined" sx={styles.container} expanded={isOpen}>
      <AccordionSummary
        sx={styles.header}
        expandIcon={<ExpandMoreIcon onClick={() => setIsOpen(!isOpen)} style={{ cursor: 'pointer' }} />}
        aria-controls="panel1-content"
        id="panel1-header"
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          <Typography variant="h6">
            {activeTagsIndex?.length
              ? `Products for tag ${activeTagsIndex.map((index: number) => ` "${tags[index]}"`).join('')}`
              : 'Products'}
          </Typography>
          <FormControl size="small" sx={{ minWidth: 140 }} onClick={(e) => e.stopPropagation()}>
            <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <MenuItem value="all">{t("bulk_status_all")}</MenuItem>
              <MenuItem value="draft">{t("draft")}</MenuItem>
              <MenuItem value="active">{t("bulk_status_active")}</MenuItem>
              <MenuItem value="publish">{t("publish")}</MenuItem>
              <MenuItem value="failed">{t("bulk_status_failed")}</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </AccordionSummary>

      <AccordionDetails>
        <BulkActionsBar
          selectedCount={selectedIds.length}
          onPublish={handleBulkPublish}
          onIssueQr={handleBulkIssueQr}
          onExport={handleBulkExport}
          onDelete={handleBulkDelete}
          onClear={() => setSelectedIds([])}
        />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', paddingLeft: '8px' }}>
          <Checkbox checked={allPageSelected} onChange={toggleSelectAll} size="small" />
          <Typography variant="caption">{t("bulk_select_page")}</Typography>
        </Box>

        <ProductFilters filter={filter} sortConfig={sortConfig} onChangeSort={handleSortChange} />

        <Box>
          {company ? (
            paginatedProducts.length > 0 ? (
              <Box>
                <CustomTabPanel value={tab} index={0}>
                  <ProductTab
                    data={paginatedProducts}
                    selectedIds={selectedIds}
                    onToggleSelect={toggleSelect}
                  />
                </CustomTabPanel>
                {hasPagination && filteredProducts.length > ITEMS_PER_PAGE && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: '32px' }}>
                    <PaginationUI
                      count={Math.ceil(filteredProducts.length / ITEMS_PER_PAGE)}
                      page={page}
                      onChange={handleChangePage}
                    />
                  </Box>
                )}
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px', padding: '48px' }}>
                <NoData
                  buttonText=""
                  description={t("bulk_empty_description")}
                />
                <Box sx={{ display: 'flex', gap: '12px', flexWrap: 'wrap', justifyContent: 'center' }}>
                  <ButtonUI type="default" text={t("add_product")} onClick={onCreateClick} />
                  <ButtonUI type="outline" text={t("bulk_import")} onClick={onImportClick} />
                </Box>
              </Box>
            )
          ) : (
            <NoData
              buttonText={t("create_company")}
              description={checkNoDataDescription(pathname)}
              onClick={() => setOpenCreateCompany(!openCreateCompany)}
            />
          )}
        </Box>
      </AccordionDetails>

      {openCreateCompany &&
        createPortal(
          <ModalOverlay onClose={() => setOpenCreateCompany(false)} openModal={openCreateCompany}>
            <CreateNewCompany setOpenModal={setOpenCreateCompany} />
          </ModalOverlay>,
          document.getElementById('portal') as HTMLElement
        )}

      {openCreateProduct &&
        createPortal(
          <ModalOverlay onClose={() => setOpenCreateProduct(false)} openModal={openCreateProduct}>
            <AddNewProductDetail setOpenModal={setOpenCreateProduct} />
          </ModalOverlay>,
          document.getElementById('portal') as HTMLElement
        )}
    </Accordion>
  )
}

export default ProductsList
